import { Icon } from '@tcc/crate';
import { useMemo } from 'react';
import { getMessage } from '@tcc/i18n';
import { FeatureAsset } from '../../types/assets';
import { ImpersonationMode } from '../../types/impersonationModes';
import { LinkDescriptor, MenuDescriptor, UrlPlaceholders } from './types';

export const useLinkDescriptors = () => {

  const menues: MenuDescriptor[] = useMemo(() => [
    {
      id: 'fields',
      glyph: Icon.set.IA_FIELDS,
      label: getMessage('sidenav.fields'),
      links: [
        {
          label: getMessage('sidenav.fields.layers'),
          href: `${UrlPlaceholders.ROOT_URL}/static/field-view/#/field-selection-web${UrlPlaceholders.QUERY_PARAMS}`,
          testId: 'farmer-nav-field-layers',
          requiredFeatureFlags: ['web.in-season'],
          isActiveRegexs: [
            /field-selection-web/i,
            /dmb/i,
          ],
        },
        {
          label: getMessage('sidenav.fields.inSeason'),
          href: `${UrlPlaceholders.ROOT_URL}/static/field-view/#/in-season${UrlPlaceholders.QUERY_PARAMS}`,
          testId: 'farmer-nav-field-in-season',
          requiredFeatureFlags: ['web.in-season'],
          isActiveRegexs: [
            /in-season/i,
          ],
        },
      ]
    },
    {
      id: 'data',
      label: getMessage('sidenav.data'),
      glyph: Icon.set.IA_DATA,
      links: [
        {
          label: getMessage('sidenav.UploadAndImport'),
          href: `${UrlPlaceholders.ROOT_URL}/static/field-view/#/data-inbox/${UrlPlaceholders.QUERY_PARAMS}`,
          testId: 'farmer-nav-import',
          allowedFeatureAssets: [FeatureAsset.DATA_INBOX],
          isActiveRegexs: [/data-inbox/i],
        },
        {
          label: getMessage('sidenav.fix'),
          href: `${UrlPlaceholders.ROOT_URL}/static/fixit-tool/index.html${UrlPlaceholders.QUERY_PARAMS}`,
          excludedByFeatureFlag: ['web.fieldview-new-arch-fixit-tool'],
          testId: 'farmer-nav-fix',
          isActiveRegexs: [/fixit/i],
        },
        {
          label: getMessage('sidenav.fix'),
          href: `${UrlPlaceholders.ROOT_URL}/static/field-view/#/fixit-tool/${UrlPlaceholders.QUERY_PARAMS}`,
          requiredFeatureFlags: ['web.fieldview-new-arch-fixit-tool'],
          testId: 'farmer-nav-fix',
          isActiveRegexs: [/fixit/i],
        },
        {
          label: getMessage('sidenav.connect'),
          href: `${UrlPlaceholders.ROOT_URL}/static/field-view/#/partners/${UrlPlaceholders.QUERY_PARAMS}`,
          testId: 'farmer-nav-apps',
          requiredFeatureFlags: ['web.connect-available-countries'],
          allowedFeatureAssets: [FeatureAsset.CONNECT],
          allowedImpersonationModes: [
            ImpersonationMode.PROXYING_ITSELF,
            ImpersonationMode.IMPERSONATION_NOT_AVAILABLE,
          ],
          isActiveRegexs: [/partners/i],
        }
      ]
    }
  ], []);

  const links: LinkDescriptor[] = useMemo(() => [
    {
      label: getMessage('sidenav.dashboard'),
      href: `${UrlPlaceholders.ROOT_URL}/static/field-view/#/dashboard${UrlPlaceholders.QUERY_PARAMS}`,
      glyph: Icon.set.IA_DASHBOARD,
      testId: 'farmer-nav-dashboard',
      requiredFeatureFlags: ['web.dashboard'],
      isActiveRegexs: [/dashboard/i],
    },
    {
      label: getMessage('sidenav.plans'),
      href: `${UrlPlaceholders.ROOT_URL}/static/field-view/#/crop-planning/${UrlPlaceholders.QUERY_PARAMS}`,
      glyph: Icon.set.IA_PLANS,
      testId: 'farmer-nav-crop-planning',
      requiredFeatureFlags: ['web.crop-planning'],
      allowedContryCodes: ['US'],
      excludedImpersonationModes: [ImpersonationMode.DEALER_PROXYING_FARMER],
      isActiveRegexs: [/crop-planning/i],
    },
    {
      label: getMessage('sidenav.scripts'),
      href: `${UrlPlaceholders.ROOT_URL}/variable-rate/index.html${UrlPlaceholders.QUERY_PARAMS}`,
      glyph: Icon.set.IA_PRESCRIPTIONS,
      testId: 'farmer-nav-prescriptions',
      allowedFeatureAssets: [FeatureAsset.MANUAL_SCRIPTING],
      excludedByFeatureFlag: ['script-creator-plugin-version', 'script-creator-operational-table-view'],
      queryParams: {
        'field-id': window.location.hash.split('/')?.[3],
      },
      isActiveRegexs: [
        /variable-rate/i,
        /script-creator/i
      ],
    },
    {
      label: getMessage('sidenav.scripts'),
      href: `${UrlPlaceholders.ROOT_URL}/static/field-view/#/variable-rate${UrlPlaceholders.QUERY_PARAMS}`,
      glyph: Icon.set.IA_PRESCRIPTIONS,
      testId: 'farmer-nav-prescriptions',
      allowedFeatureAssets: [FeatureAsset.MANUAL_SCRIPTING],
      requiredFeatureFlags: ['script-creator-plugin-version'],
      excludedByFeatureFlag: ['script-creator-operational-table-view'],
      queryParams: {
        'field-id': window.location.hash.split('/')?.[3],
      },
      isActiveRegexs: [
        /variable-rate/i,
        /script-creator/i
      ],
    },
    {
      label: getMessage('sidenav.scripts'),
      href: `${UrlPlaceholders.ROOT_URL}/static/field-view/#/script-creator${UrlPlaceholders.QUERY_PARAMS}`,
      glyph: Icon.set.IA_PRESCRIPTIONS,
      testId: 'farmer-nav-prescriptions',
      allowedFeatureAssets: [FeatureAsset.MANUAL_SCRIPTING],
      requiredFeatureFlags: ['script-creator-operational-table-view'],
      isActiveRegexs: [
        /variable-rate/i,
        /script-creator/i
      ],
    },
    {
      label: getMessage('sidenav.fields'),
      href: `${UrlPlaceholders.ROOT_URL}/static/field-view/#/field-selection-web${UrlPlaceholders.QUERY_PARAMS}`,
      glyph: Icon.set.IA_FIELDS,
      testId: 'farmer-nav-fields',
      excludedByFeatureFlag: ['web.in-season'],
      isActiveRegexs: [
        /field-selection-web/i,
        /dmb/i,
      ],
    },
    {
      label: getMessage('sidenav.insights'),
      href: `${UrlPlaceholders.ROOT_URL}/static/field-view/#/reports/yield-report${UrlPlaceholders.QUERY_PARAMS}`,
      glyph: Icon.set.IA_REPORTS,
      testId: 'farmer-nav-reports',
      allowedFeatureAssets: [FeatureAsset.YIELD_ANALYSIS],
      isActiveRegexs: [/reports/i],
    },
    {
      label: getMessage('sidenav.ask'),
      href: 'https://climate.com/static/field-view/#/ask-fieldview-web/',
      glyph: Icon.set.IA_ASK,
      testId: 'farmer-nav-askfieldview',
      requiredFeatureFlags: ['web.ask-fieldview-web'],
      isActiveRegexs: [/ask-fieldview-web/i],
    },
    {
      label: getMessage('sidenav.experimentsLabz'),
      href: `https://climate.com/static/labz/${UrlPlaceholders.QUERY_PARAMS}`,
      glyph: Icon.set.IA_EXPERIMENTS,
      testId: 'farmer-nav-experiments-labz',
      requiredFeatureFlags: ['farmview.experiment-labz'],
      isActiveRegexs: [/labz/i],
    },
    {
      label: getMessage('sidenav.programs'),
      href: `${UrlPlaceholders.ROOT_URL}/static/field-view/#/programs/${UrlPlaceholders.QUERY_PARAMS}`,
      glyph: Icon.set.IA_PROGRAMS,
      testId: 'farmer-nav-programs',
      requiredFeatureFlags: ['web.programs-plugin'],
      isActiveRegexs: [/programs/i],
    },
    {
      label: getMessage('sidenav.dataHub'),
      href: `${UrlPlaceholders.ROOT_URL}/static/field-view/#/data-hub/${UrlPlaceholders.QUERY_PARAMS}`,
      glyph: Icon.set.IA_REPORTS,
      testId: 'farmer-nav-data-hub',
      requiredFeatureFlags: ['data-hub'],
      isActiveRegexs: [/data-hub/i],
    },
  ], []);

  return {
    linkDescriptors: links,
    menuDescriptors: menues
  };
};
